import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Users, Plus, Search, Download, Upload, Phone, Mail, CreditCard,
  User, DollarSign, Filter, ChevronLeft, ChevronRight, Edit3, Trash2,
  X, AlertTriangle, Check, Ban, Shield, Calendar, MessageSquare,
  MoreHorizontal, Wallet, TrendingUp, TrendingDown
} from 'lucide-react';
import toast from 'react-hot-toast';
import PageHeader from '../components/common/PageHeader';
import Button from '../components/common/Button';
import Modal from '../components/common/Modal';
import ConfirmDialog from '../components/common/ConfirmDialog';
import Badge from '../components/common/Badge';
import EmptyState from '../components/common/EmptyState';
import Skeleton from '../components/common/Skeleton';
import StatCard from '../components/common/StatCard';
import CustomerCard from '../components/customers/CustomerCard';
import CustomerForm from '../components/customers/CustomerForm';
import CreditLedger from '../components/customers/CreditLedger';
import {
  fetchCustomers, createCustomer, updateCustomer, deleteCustomer,
  getCreditSummary, recordPayment, blockCustomer, getTransactions,
  clearCustomer, setPage
} from '../redux/slices/customerSlice';

const containerVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.05 } },
};

export default function Customers() {
  const dispatch = useDispatch();
  const { customers, loading, totalPages, currentPage, creditSummary, transactions } = useSelector(
    (state) => state.customers
  );

  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [categoryFilter, setCategoryFilter] = useState('All');
  const [showForm, setShowForm] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentNote, setPaymentNote] = useState('');
  const [paymentLoading, setPaymentLoading] = useState(false);

  useEffect(() => {
    dispatch(fetchCustomers({ page: 1, limit: 50 }));
    dispatch(getCreditSummary());
  }, [dispatch]);

  const handlePageChange = (page) => {
    dispatch(setPage(page));
    dispatch(fetchCustomers({ page, limit: 50 }));
  };

  const filteredCustomers = useMemo(() => {
    let result = customers;
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(
        (c) =>
          c.name?.toLowerCase().includes(q) ||
          c.phone?.toLowerCase().includes(q) ||
          c.cnic?.toLowerCase().includes(q)
      );
    }
    if (statusFilter === 'Active') {
      result = result.filter((c) => c.active !== false);
    } else if (statusFilter === 'Blocked') {
      result = result.filter((c) => c.active === false || c.blocked);
    } else if (statusFilter === 'Has Credit') {
      result = result.filter((c) => (c.currentCredit || 0) > 0);
    }
    if (categoryFilter !== 'All') {
      result = result.filter((c) => c.category === categoryFilter);
    }
    return result;
  }, [customers, searchQuery, statusFilter, categoryFilter]);

  const handleCreateCustomer = async (data) => {
    try {
      await dispatch(createCustomer(data)).unwrap();
      setShowForm(false);
      toast.success('Customer created successfully');
    } catch (err) {
      toast.error(err?.message || 'Failed to create customer');
    }
  };

  const handleUpdateCustomer = async (data) => {
    try {
      await dispatch(updateCustomer({ id: editingCustomer.customerId || editingCustomer._id || editingCustomer.id, customerData: data })).unwrap();
      setEditingCustomer(null);
      setShowForm(false);
      toast.success('Customer updated');
    } catch (err) {
      toast.error(err?.message || 'Failed to update customer');
    }
  };

  const handleDeleteCustomer = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await dispatch(deleteCustomer(deleteTarget.customerId || deleteTarget._id || deleteTarget.id)).unwrap();
      setDeleteTarget(null);
      toast.success('Customer deleted');
    } catch (err) {
      toast.error(err?.message || 'Failed to delete customer');
    } finally {
      setDeleting(false);
    }
  };

  const handleRecordPayment = async () => {
    if (!selectedCustomer || !paymentAmount) return;
    setPaymentLoading(true);
    try {
      await dispatch(recordPayment({
        id: selectedCustomer.customerId || selectedCustomer._id || selectedCustomer.id,
        paymentData: { amount: parseFloat(paymentAmount), notes: paymentNote, paymentMethod: 'CASH' }
      })).unwrap();
      setShowPaymentModal(false);
      setPaymentAmount('');
      setPaymentNote('');
      dispatch(getCreditSummary());
      toast.success('Payment recorded successfully');
    } catch (err) {
      toast.error(err?.message || 'Failed to record payment');
    } finally {
      setPaymentLoading(false);
    }
  };

  const handleBlockCustomer = async (customer) => {
    try {
      await dispatch(blockCustomer({ id: customer.customerId || customer._id || customer.id, reason: 'Blocked by operator' })).unwrap();
      toast.success('Customer blocked');
    } catch (err) {
      toast.error(err?.message || 'Failed to block customer');
    }
  };

  const handleSendReminder = (customer) => {
    toast.success(`Reminder sent to ${customer.fullName || customer.name}`);
  };

  const categories = ['All', 'Regular', 'Wholesale', 'VIP', 'Staff', 'Other'];

  const handleSelectCustomer = (customer) => {
    setSelectedCustomer(customer);
    dispatch(getTransactions({ id: customer.customerId || customer._id || customer.id, params: { page: 1, limit: 50 } }));
  };

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="p-4 md:p-6 space-y-6"
    >
      <PageHeader
        title="Customer Management"
        description="Manage your customers and credit"
        actions={
          <div className="flex items-center gap-2">
            <Button variant="secondary" size="sm" icon={Download}>
              Export
            </Button>
            <Button variant="secondary" size="sm" icon={Upload}>
              Import
            </Button>
            <Button variant="primary" size="sm" icon={Plus} onClick={() => { setEditingCustomer(null); setShowForm(true); }}>
              Add Customer
            </Button>
          </div>
        }
      />

      {creditSummary && (
        <div className="grid grid-cols-3 gap-4">
          <StatCard
            icon={Wallet}
            label="Total Credit"
            value={creditSummary.totalCredit || 0}
            color="blue"
          />
          <StatCard
            icon={TrendingUp}
            label="Outstanding"
            value={creditSummary.outstanding || 0}
            color="red"
          />
          <StatCard
            icon={TrendingDown}
            label="This Month Collection"
            value={creditSummary.monthlyCollection || 0}
            color="green"
          />
        </div>
      )}

      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by name, phone, or CNIC..."
            className="w-full pl-9 pr-3 py-2.5 text-sm bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-xl focus:border-primary-500 focus:outline-none text-gray-900 dark:text-gray-100 placeholder-gray-400"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-3 py-2.5 text-sm bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-700 dark:text-gray-300 focus:outline-none"
        >
          <option value="All">All Status</option>
          <option value="Active">Active</option>
          <option value="Blocked">Blocked</option>
          <option value="Has Credit">Has Credit</option>
        </select>
        <select
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value)}
          className="px-3 py-2.5 text-sm bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-700 dark:text-gray-300 focus:outline-none"
        >
          {categories.map((cat) => (
            <option key={cat} value={cat}>{cat}</option>
          ))}
        </select>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-4">
          {loading ? (
            <div className="space-y-3">
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} variant="card" />
              ))}
            </div>
          ) : filteredCustomers.length === 0 ? (
            <EmptyState
              icon={Users}
              title="No customers found"
              description={searchQuery ? 'Try adjusting your search' : 'Add your first customer'}
              actionLabel="Add Customer"
              onAction={() => { setEditingCustomer(null); setShowForm(true); }}
            />
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <AnimatePresence mode="popLayout">
                {filteredCustomers.map((customer) => (
                  <motion.div
                    key={customer._id || customer.id}
                    layout
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    onClick={() => handleSelectCustomer(customer)}
                    className={`cursor-pointer transition-all ${
                      selectedCustomer?._id === customer._id || selectedCustomer?.id === customer.id
                        ? 'ring-2 ring-primary-500 rounded-xl'
                        : ''
                    }`}
                  >
                    <CustomerCard
                      customer={customer}
                      onAddPayment={(c) => {
                        setSelectedCustomer(c);
                        setShowPaymentModal(true);
                      }}
                      onViewLedger={(c) => handleSelectCustomer(c)}
                      onSendReminder={(c) => handleSendReminder(c)}
                    />
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>
          )}

          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 pt-2">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage <= 1}
                className="p-2 rounded-lg text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <ChevronLeft size={18} />
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                <button
                  key={page}
                  onClick={() => handlePageChange(page)}
                  className={`w-9 h-9 rounded-lg text-sm font-medium transition-colors ${
                    page === currentPage
                      ? 'bg-primary-600 text-white'
                      : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                  }`}
                >
                  {page}
                </button>
              ))}
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage >= totalPages}
                className="p-2 rounded-lg text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <ChevronRight size={18} />
              </button>
            </div>
          )}
        </div>

        <div className="space-y-4">
          {selectedCustomer ? (
            <>
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Customer Details</h3>
                <button
                  onClick={() => setSelectedCustomer(null)}
                  className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                >
                  <X size={16} />
                </button>
              </div>

              <CreditLedger
                customer={selectedCustomer}
                transactions={transactions}
                loading={loading}
                onExport={() => toast.success('Ledger exported')}
                onPrint={() => toast.success('Printing ledger...')}
              />

              <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
                <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">Quick Actions</h4>
                <div className="flex flex-wrap gap-2">
                  <Button
                    variant="primary"
                    size="sm"
                    icon={DollarSign}
                    onClick={() => setShowPaymentModal(true)}
                  >
                    Add Payment
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    icon={MessageSquare}
                    onClick={() => handleSendReminder(selectedCustomer)}
                  >
                    Send Reminder
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    icon={Edit3}
                    onClick={() => {
                      setEditingCustomer(selectedCustomer);
                      setShowForm(true);
                    }}
                  >
                    Edit
                  </Button>
                  <Button
                    variant="danger"
                    size="sm"
                    icon={Ban}
                    onClick={() => handleBlockCustomer(selectedCustomer)}
                  >
                    Block
                  </Button>
                </div>
              </div>
            </>
          ) : (
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
              <EmptyState
                icon={Users}
                title="Select a customer"
                description="Click on a customer to view their details and credit ledger"
              />
            </div>
          )}
        </div>
      </div>

      <Modal
        isOpen={showForm}
        onClose={() => { setShowForm(false); setEditingCustomer(null); }}
        size="lg"
        title={editingCustomer ? 'Edit Customer' : 'Add Customer'}
      >
        <CustomerForm
          customer={editingCustomer}
          onSubmit={editingCustomer ? handleUpdateCustomer : handleCreateCustomer}
          onCancel={() => { setShowForm(false); setEditingCustomer(null); }}
          loading={loading}
        />
      </Modal>

      <ConfirmDialog
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDeleteCustomer}
        title="Delete Customer"
        message={`Are you sure you want to delete "${deleteTarget?.name}"? This action cannot be undone.`}
        confirmLabel="Delete"
        loading={deleting}
        variant="danger"
      />

      <Modal
        isOpen={showPaymentModal}
        onClose={() => { setShowPaymentModal(false); setPaymentAmount(''); setPaymentNote(''); }}
        size="sm"
        title={`Record Payment - ${selectedCustomer?.name || ''}`}
      >
        <div className="space-y-4">
          <div>
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">Payment Amount</label>
            <div className="relative">
              <DollarSign size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="number"
                min="0"
                step="0.01"
                value={paymentAmount}
                onChange={(e) => setPaymentAmount(e.target.value)}
                placeholder="Enter amount"
                className="w-full pl-9 pr-3 py-2.5 text-sm bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-gray-900 dark:text-gray-100 focus:outline-none focus:border-primary-500 placeholder-gray-400"
                autoFocus
              />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">Note (optional)</label>
            <input
              type="text"
              value={paymentNote}
              onChange={(e) => setPaymentNote(e.target.value)}
              placeholder="Payment note"
              className="w-full px-3 py-2.5 text-sm bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-gray-900 dark:text-gray-100 focus:outline-none focus:border-primary-500 placeholder-gray-400"
            />
          </div>
          <div className="flex gap-3 pt-2">
            <Button variant="secondary" fullWidth onClick={() => { setShowPaymentModal(false); setPaymentAmount(''); setPaymentNote(''); }}>
              Cancel
            </Button>
            <Button variant="primary" fullWidth onClick={handleRecordPayment} loading={paymentLoading} disabled={!paymentAmount}>
              Record Payment
            </Button>
          </div>
        </div>
      </Modal>
    </motion.div>
  );
}
